// Step 5d Phase 2 — provisional overlay (v2 §4).
//
// Merges the user's QUEUED ops onto a bookings list for RENDER ONLY. Queued creates
// become synthetic rows (id `pending-op:{client_op_id}`); queued edits/cancels shadow
// their target row with the proposed values / a cancel-queued marker. Synthetic and
// shadowed rows carry `_pending_op` so chips/detail render the provisional treatment.
// NOTHING here is written to the `bookings` store or sent to the API — the overlay is
// recomputed from the live queue every render pass.

import type { Booking, Participant, Allocation, ExternalAircraftSource, ParticipantCapacity, BookingStatus, Base } from '../opsBoardTypes';
import type { QueuedOp } from './db';

interface PayloadParticipant {
  user_id?: string;
  capacity?: ParticipantCapacity;
  external_name?: string;
  rank?: number | null;
}
interface PayloadAllocation {
  aircraft_registration?: string;
  asset_id?: string;
  external_aircraft_source?: ExternalAircraftSource;
  external_aircraft_type?: string;
  external_aircraft_registration?: string;
  external_aircraft_invoicer?: string;
}

function participantsFrom(payload: Record<string, unknown>, opKey: string): Participant[] {
  const raw = (payload.participants as PayloadParticipant[] | undefined) ?? [];
  return raw.map((p, i) => ({
    id: `${opKey}-p${i}`,
    user_id: p.user_id ?? null,
    // Mirror the backend roleForCapacity: student AND client occupy the role-'student' person line.
    role: (p.capacity === 'student' || p.capacity === 'client') ? 'student' as const : 'instructor' as const,
    capacity: p.capacity ?? 'instructor',
    external_name: p.external_name ?? null,
    rank: p.rank ?? null,
    notified: false,
    user: null, // names resolve via the board's reference lookups
  }));
}

function allocationsFrom(payload: Record<string, unknown>, opKey: string): Allocation[] {
  const raw = (payload.allocations as PayloadAllocation[] | undefined) ?? [];
  return raw.map((a, i) => ({
    id: `${opKey}-a${i}`,
    aircraft_registration: a.aircraft_registration ?? null,
    asset_id: a.asset_id ?? null,
    external_aircraft_source: a.external_aircraft_source ?? null,
    external_aircraft_type: a.external_aircraft_type ?? null,
    external_aircraft_registration: a.external_aircraft_registration ?? null,
    external_aircraft_invoicer: a.external_aircraft_invoicer ?? null,
    approval_status: 'not_required' as const,
    booking_confirmed_via: null,
    booking_confirmation_note: null,
    booking_confirmed_at: null,
  }));
}

function synthesizeCreate(op: QueuedOp, baseTz: (baseId: string) => string): Booking {
  const p = op.payload;
  const participants = participantsFrom(p, op.key);
  const firstOf = (role: 'instructor' | 'student') => participants.find((x) => x.role === role && x.user_id != null)?.user_id ?? null;
  return {
    id: `pending-op:${op.client_op_id}`,
    base_id: (p.base_id as string) ?? '',
    pilot_id: firstOf('instructor'),
    trainee_id: firstOf('student'),
    trainee: null,
    activity_id: (p.activity_id as string | undefined) ?? null,
    booking_kind_id: (p.booking_kind_id as string | undefined) ?? null,
    // Feature D — display-only: surface the provisional chip's title from the already-queued op
    // payload (no sync/transport/storage change). cancel_reason_id n/a on a create.
    title: (p.title as string | undefined) ?? null,
    cancel_reason_id: null,
    activity_note: (p.notes as string | undefined) ?? null,
    // Staff-only note + reminder arm flag from the queued payload (no sync/transport/storage
    // change: internal_note's API name and column name are the same, so unlike notes it needs
    // no alias). reminder_sent_at is server-owned and unknowable until replay.
    internal_note: (p.internal_note as string | undefined) ?? null,
    reminder_enabled: !!p.reminder_enabled,
    reminder_sent_at: null,
    block_start_at: (p.block_start_at as string | undefined) ?? null,
    dep_time_planned: null,
    arr_time_planned: (p.arr_time_planned as string | undefined) ?? null,
    dep_timezone: baseTz((p.base_id as string) ?? ''),
    long_brief_mins: null,
    short_brief_mins: null,
    activity_mins: (p.activity_mins as number | undefined) ?? null,
    buf_pre_mins: 30,
    buf_post_mins: 30,
    booking_status: ((p.booking_status as BookingStatus | undefined) ?? 'confirmed'),
    approval_status: 'not_required', // unknowable until replay — the provisional badge IS the honest signal
    student_notified: false,
    outside_hours: false,
    modified_after_notify: false,
    modify_notified_at: null,
    modify_notified_by: null,
    cancel_reason: null,
    cancelled_at: null,
    cancelled_by: null,
    maintenance_returned_at: null,
    maintenance_return_confirmed_by: null,
    maintenance_late_return_reason: null,
    participants,
    max_participants: (p.max_participants as number | undefined) ?? null,
    participants_limit_hard: !!p.participants_limit_hard,
    waitlisted: false,
    waitlisted_at: null,
    waitlist_allocated_by: null,
    waitlist_allocated_at: null,
    // Recurring (Phase 2): an offline create is never a series occurrence (series are generated
    // server-side, online), so these are inert on a provisional row.
    series_id: null,
    series_occurrence_date: null,
    series_overridden: false,
    series_clamped: false,
    series_assignee_conflict: false,
    updated_at: op.client_created_at,
    asset_allocations: allocationsFrom(p, op.key),
    _pending_op: { queueKey: op.key, kind: 'create' },
  };
}

function shadowEdit(target: Booking, op: QueuedOp): Booking {
  const p = op.payload;
  const has = (k: string) => Object.prototype.hasOwnProperty.call(p, k);
  return {
    ...target,
    ...(has('base_id') && p.base_id ? { base_id: p.base_id as string } : {}),
    ...(has('block_start_at') ? { block_start_at: (p.block_start_at as string | null) ?? null } : {}),
    ...(has('arr_time_planned') ? { arr_time_planned: (p.arr_time_planned as string | null) ?? null } : {}),
    ...(has('activity_mins') ? { activity_mins: (p.activity_mins as number | null) ?? null } : {}),
    ...(has('activity_id') ? { activity_id: (p.activity_id as string | null) ?? null } : {}),
    ...(has('booking_kind_id') ? { booking_kind_id: (p.booking_kind_id as string | null) ?? null } : {}),
    ...(has('notes') ? { activity_note: (p.notes as string | null) ?? null } : {}),
    ...(has('internal_note') ? { internal_note: (p.internal_note as string | null) ?? null } : {}),
    ...(has('reminder_enabled') ? { reminder_enabled: !!p.reminder_enabled } : {}),
    ...(has('booking_status') && p.booking_status ? { booking_status: p.booking_status as BookingStatus } : {}),
    ...(has('max_participants') ? { max_participants: (p.max_participants as number | null) ?? null } : {}),
    ...(has('participants_limit_hard') ? { participants_limit_hard: !!p.participants_limit_hard } : {}),
    ...(has('participants') ? { participants: participantsFrom(p, op.key) } : {}),
    ...(has('allocations') ? { asset_allocations: allocationsFrom(p, op.key) } : {}),
    _pending_op: { queueKey: op.key, kind: 'edit' as const },
  };
}

/** Merge the queue onto a bookings list (render-only). Later ops shadow earlier ones. */
export function overlayQueuedOps(bookings: Booking[], ops: QueuedOp[], bases: Base[]): Booking[] {
  if (ops.length === 0) return bookings;
  const tzOf = (baseId: string) => bases.find((b) => b.id === baseId)?.timezone ?? 'Australia/Darwin';
  const byId = new Map(bookings.map((b) => [b.id, b]));
  const synthetic: Booking[] = [];
  for (const op of ops) {
    // waitlist_join renders as a provisional create (it becomes waitlisted/booked at
    // sync); relocation has no Task to shadow (aircraft-level — no board overlay).
    if (op.op_type === 'booking_create' || op.op_type === 'waitlist_join') {
      synthetic.push(synthesizeCreate(op, tzOf));
      continue;
    }
    if (op.op_type === 'relocation') continue;
    const targetId = op.payload.task_id as string | undefined;
    if (!targetId) continue;
    const target = byId.get(targetId);
    if (!target) continue; // outside the loaded window — nothing to shadow
    if (op.op_type === 'booking_edit') {
      byId.set(targetId, shadowEdit(target, op));
    } else if (op.op_type === 'maintenance_return') {
      // Shadow only the provisional marker — the lifecycle states stay server-truth
      // until the drain confirms the return.
      byId.set(targetId, { ...target, _pending_op: { queueKey: op.key, kind: 'edit' as const } });
    } else { // booking_cancel | waitlist_leave
      byId.set(targetId, { ...target, _pending_op: { queueKey: op.key, kind: 'cancel' as const } });
    }
  }
  return [...byId.values(), ...synthetic];
}
